import localforage from "localforage";
import moment from "moment";

import { getComputedTimelineItemsKeyName } from "./fast";
import packageJson from "../../package.json";
import { OFFSET_HOURS } from "../constants/fast";

import type Timeslot from "okast-timeslot";
import type { VariantTimeslot } from "okast-timeslot";

import type { Media, TimelineItems } from "../types";

interface SetTimelineItemsParams {
  timeslot: Timeslot;
  variantTimeslots: VariantTimeslot[];
  timelineContent: Record<string, Media>;
  mediaIdListOnCurrentDay: string[];
  mediaIdListOnNextDay: string[];
  mediaIdListOnPreviousDay: string[];
  currentDay: string;
  previousDay: string;
  nextDay: string;
  isClustered: boolean;
}

export const defaultTimelineItems: TimelineItems = {
  timelineMediaIdListByStartDate: {},
  timeslotsByStartDate: {},
  timelineDataPartialFetched: {},
  timelineContent: {},
};

const KEY_BASE = "timelineItems";

export const timelineItemsKey = `${KEY_BASE}-${packageJson.version}`;

const cleanLocalStorage = async (): Promise<void> => {
  const existingKeys = await localforage.keys();

  existingKeys
    .filter((key) => key.includes(KEY_BASE))
    .forEach(async (key) => localforage.removeItem(key));
};

export const getLocalStorageTimelineItems =
  async (): Promise<TimelineItems> => {
    try {
      const existingTimelineItems =
        await localforage.getItem<TimelineItems>(timelineItemsKey);

      if (existingTimelineItems) return existingTimelineItems;

      await cleanLocalStorage();
    } catch (error) {
      console.error(`Get timelineItems from local storage failed : ${error}`);
    }

    return defaultTimelineItems;
  };

export const setTimelineItems = async ({
  timelineContent,
  mediaIdListOnCurrentDay,
  mediaIdListOnNextDay,
  mediaIdListOnPreviousDay,
  timeslot,
  variantTimeslots,
  currentDay,
  previousDay,
  nextDay,
  isClustered,
}: SetTimelineItemsParams): Promise<void> => {
  const computedItemsKeyName = getComputedTimelineItemsKeyName(isClustered);

  try {
    const localStorageTimelineItems = await getLocalStorageTimelineItems();

    const {
      timeslotsByStartDate: localStorageTimeslotsByStartDate,
      timelineMediaIdListByStartDate:
        localStorageTimelineMediaIdListByStartDate,
      timelineDataPartialFetched: localStorageTimelineDataPartialFetched,
      timelineContent: localStorageTimelineContent,
    } = localStorageTimelineItems;

    const hasDataOnPreviousDay = Array.isArray(
      localStorageTimelineMediaIdListByStartDate[previousDay]?.[
        computedItemsKeyName
      ],
    );

    const hasDataOnNextDay = Array.isArray(
      localStorageTimelineMediaIdListByStartDate[nextDay]?.[
        computedItemsKeyName
      ],
    );

    const isPartialFetchedOnPreviousDay =
      localStorageTimelineDataPartialFetched[previousDay]?.[
        computedItemsKeyName
      ] ?? !hasDataOnPreviousDay;

    const isPartialFetchedOnNextDay =
      localStorageTimelineDataPartialFetched[nextDay]?.[computedItemsKeyName] ??
      !hasDataOnNextDay;

    const localStorageMediaIdListOnNextDay =
      localStorageTimelineMediaIdListByStartDate[nextDay]?.[
        computedItemsKeyName
      ] ?? [];

    const localStorageMediaIdListOnPreviousDay =
      localStorageTimelineMediaIdListByStartDate[previousDay]?.[
        computedItemsKeyName
      ] ?? [];

    const localStorageMediaIdListOnPreviousDayAm =
      localStorageMediaIdListOnPreviousDay.filter((mediaId) => {
        const media = localStorageTimelineContent[mediaId];

        if (!media) return false;

        return moment(media.endTime).isBefore(
          moment(currentDay).startOf("day").subtract(OFFSET_HOURS, "hours"),
        );
      });

    const localStorageMediaIdListOnNextDayPm =
      localStorageMediaIdListOnNextDay.filter((mediaId) => {
        const media = localStorageTimelineContent[mediaId];

        if (!media) return false;

        return moment(media.startTime).isAfter(
          moment(currentDay).endOf("day").add(OFFSET_HOURS, "hours"),
        );
      });

    const timeslotsByStartDateUpdated = {
      ...localStorageTimeslotsByStartDate,
      [currentDay]: {
        ...localStorageTimeslotsByStartDate[currentDay],
        [computedItemsKeyName]: {
          timeslot,
          variantTimeslots,
        },
      },
    };

    const timelineMediaIdListByStartDateUpdated = {
      ...localStorageTimelineMediaIdListByStartDate,
      [previousDay]: {
        ...localStorageTimelineMediaIdListByStartDate[previousDay],
        [computedItemsKeyName]: [
          ...localStorageMediaIdListOnPreviousDayAm,
          ...mediaIdListOnPreviousDay,
        ],
      },
      [currentDay]: {
        ...localStorageTimelineMediaIdListByStartDate[currentDay],
        [computedItemsKeyName]: mediaIdListOnCurrentDay,
      },
      [nextDay]: {
        ...localStorageTimelineMediaIdListByStartDate[nextDay],
        [computedItemsKeyName]: [
          ...localStorageMediaIdListOnNextDayPm,
          ...mediaIdListOnNextDay,
        ],
      },
    };

    const timelineContentUpdated = {
      ...localStorageTimelineContent,
      ...timelineContent,
    };

    const timelineDataPartialFetchedUpdated = {
      ...localStorageTimelineDataPartialFetched,
      [previousDay]: {
        ...localStorageTimelineDataPartialFetched[previousDay],
        [computedItemsKeyName]: isPartialFetchedOnPreviousDay,
      },
      [currentDay]: {
        ...localStorageTimelineDataPartialFetched[currentDay],
        [computedItemsKeyName]: false,
      },
      [nextDay]: {
        ...localStorageTimelineDataPartialFetched[nextDay],
        [computedItemsKeyName]: isPartialFetchedOnNextDay,
      },
    };

    await localforage.setItem(timelineItemsKey, {
      timelineMediaIdListByStartDate: timelineMediaIdListByStartDateUpdated,
      timeslotsByStartDate: timeslotsByStartDateUpdated,
      timelineDataPartialFetched: timelineDataPartialFetchedUpdated,
      timelineContent: timelineContentUpdated,
    });
  } catch (error) {
    throw new Error(`Set timelineItems on local storage failed : ${error}`);
  }
};

export const updateTimelineItem = async (
  mediaToUpdate: Pick<Media, "uuid" | "startTime">,
): Promise<void> => {
  const timelineItems =
    await localforage.getItem<TimelineItems>(timelineItemsKey);

  const media = timelineItems?.timelineContent[mediaToUpdate.uuid];

  if (!media) return;

  const newStartTime = moment(mediaToUpdate.startTime);

  const updatedMedia: Media = {
    ...media,
    startTime: mediaToUpdate.startTime,
    endTime: moment(
      newStartTime.add(media.duration, "millisecond"),
    ).toISOString(),
  };

  await localforage.setItem<TimelineItems>(timelineItemsKey, {
    ...timelineItems,
    timelineContent: {
      ...timelineItems.timelineContent,
      [mediaToUpdate.uuid]: updatedMedia,
    },
  });
};
